import { useLicenseStore } from '@Models/useLicenseModel';
import {
  Box,
  Divider,
  Flex,
  Text,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useColorStyle,
  useColorMode,
  Scrollbar,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import i18nInst from 'src/i18n';
import { useEffect, useState } from 'react';
import config from 'src/config';

interface LicenseInfo {
  tenantId: string;
  enabled: boolean;
  autoRenew: number;
  expirationDate: string;
  graceEndDate: string;
  lastModifiedDate: string;
  seats: number;
  status: string;
  type: string;
  salesType: string;
}

const LicenseTable = (props: { product: string; license: LicenseInfo }) => {
  const { product, license } = props;
  const { t } = useTranslation();
  const licenseInfo = [
    {
      label: t('administration.license.product'),
      value: (
        <Box
          fontWeight={700}
          dangerouslySetInnerHTML={{
            __html: product,
          }}
        />
      ),
    },
    {
      label: t('administration.license.type'),
      value: t(`administration.license.${license.type}`),
    },
    { label: t('administration.license.userCount'), value: license.seats },
    {
      label: t('administration.license.status'),
      value: t(`administration.license.${license.status}`),
    },
    {
      label: t('administration.license.expirationDate'),
      value: dayjs(license.expirationDate).format(
        t('common.momentDateFormat') + '(UTCZ)'
      ),
    },
  ];

  return (
    <Table layout="table">
      <TableBody>
        {licenseInfo.map((info, index) => (
          <TableRow key={index}>
            <TableCell minWidth={40} width={200} border="1px solid #5E5E5E">
              {info.label}
            </TableCell>
            <TableCell minWidth={40} width={600} border="1px solid #5E5E5E">
              {info.value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const License = () => {
  const licenseModule = useLicenseStore();
  const casLicense = licenseModule.license.CAS as LicenseInfo;
  const sfLicense = licenseModule.license['CAS-SF'] as LicenseInfo;
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState<string | undefined>(undefined);
  const licensePath =
    config.ui.prefix + `/license/${i18nInst.language}/license.html`;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const fetchHTMLContent = async () => {
    try {
      const response = await fetch(licensePath); // 根据文件路径进行调整
      const htmlString = await response.text();
      const fontColor =
        colorMode === 'dark'
          ? 'color: rgba(255, 255, 255, 0.47)'
          : 'color: rgba(0,0,0,.92)';
      setHtmlContent(htmlString.replace(/color: #333/gm, fontColor));
    } catch (error) {
      console.error('Failed to fetch HTML:', error);
    }
  };

  useEffect(() => {
    fetchHTMLContent().then();
  });
  return (
    <Flex direction="column">
      <Box pl="24px" width="900px" py="16px">
        <Text>{t('administration.license.warning')}</Text>
        <br />
        <Text
          dangerouslySetInnerHTML={{
            __html: t('common:common.copyRight', {
              year: new Date().getFullYear(),
            }),
          }}
        />
      </Box>
      <Divider width="100%" />
      <Box pl="24px">
        <Text
          fontWeight="600"
          fontSize="18px"
          lineHeight="24px"
          pt="24px"
          pb="14px"
        >
          {t('administration.license.licenseInfo')}
        </Text>
        {casLicense && casLicense.enabled && (
          <LicenseTable
            product={t('administration.license.productName.tmcas')}
            license={casLicense}
          />
        )}
        {sfLicense && sfLicense.enabled && (
          <>
            <br />
            <LicenseTable
              product={t('administration.license.productName.tmcassf')}
              license={sfLicense}
            />
          </>
        )}
      </Box>
      <Flex pl="24px" pb="40px" pt="24px" direction="column" gap={14}>
        <Text fontWeight="600" fontSize="18px" lineHeight="24px">
          {t('administration.license.licenseAgreement')}
        </Text>

        <Scrollbar
          height={250}
          width={814}
          overflow="scroll"
          resize="both"
          border="1px solid #5E5E5E"
        >
          <Text
            color="white"
            background={colorStyle.background.primary}
            whiteSpace="nowrap"
            p="8px 8px"
            dangerouslySetInnerHTML={{
              __html: htmlContent || '',
            }}
          />
        </Scrollbar>

        <Text>{t('administration.license.trendLogo')}</Text>
      </Flex>
    </Flex>
  );
};
export default License;
